import classNames from "classnames";
import s from './index.module.css';
import { useState } from 'react';
import { EyeOutlined, EyeInvisibleOutlined, CopyTwoTone } from "@ant-design/icons";
import { useAddAlert } from "@/hooks/useAddAlert";

/**
 * Пропсы компонента Text
 */
interface TextProps {
  /**
   * Массив текстовых строк для отображения
   * - Первый элемент отображается всегда
   * - Остальные элементы отображаются при нажатии на кнопку "показать все"
   */
  texts: string[];
  
  /**
   * Тип стиля текстового блока
   * @default 'blue'
   */
  type?: 'blue' | 'grey' | 'lightGray';
  
  /**
   * Заголовок, отображаемый над текстом
   * @default ''
   */
  title?: string;
  
  /**
   * Настройки функционала копирования
   */
  copy?: {
    /**
     * Флаг, показывать ли иконку копирования
     * @default false
     */
    show: boolean;
    
    /**
     * Текст уведомления после копирования
     * Если не указан, уведомление не показывается
     */
    copyText?: string;
  };
  
  className?: string;
}

/**
 * Компонент для отображения текста с возможностями:
 * - Показ/скрытие дополнительного текста
 * - Копирование текста в буфер обмена
 * - Различные стили оформления
 * 
 * @example
 * <Text 
 *   texts={['Основной текст', 'Дополнительный текст']}
 *   type="grey"
 *   title="Пример:"
 *   copy={{ show: true, copyText: 'Текст скопирован!' }}
 * />
*/

const Text: React.FC<TextProps> = ({ 
  texts, 
  type = 'blue', 
  title = '', 
  copy = { show: false, copyText: '' },
  className = ''
}) => {
  const addAlert = useAddAlert();
  const [showAll, setShowAll] = useState<boolean>(false);
  
  let classForField = s.blue;
  if (type === 'grey') {
    classForField = s.grey;
  } else if (type === 'lightGray') {
    classForField = s.lightGray;
  }

  if (!Array.isArray(texts) || texts.length === 0) {
    return null;
  }

  const displayText = showAll 
    ? texts.join(', ') 
    : texts[0];
  
  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(displayText);
      if (!!copy.copyText) {
        addAlert({ title: 'Скопировано!' });
      }
    } catch (err) {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось скопировать текст',
        type: 'error'
      });
    }
  };

  const iconProps = {
    onPointerEnterCapture: () => {},
    onPointerLeaveCapture: () => {},
  };

  return (
    <div className={classNames(s.wrapper, {[className]: !!className})}>
      {title && <span className={s.title}>{title}</span>}
      
      <div className={classNames(s.text, classForField)}>
        {displayText}

        {texts.length > 1 && (
          <>
            {!showAll && ',...'}
            <button 
              className={s.button}
              onClick={() => setShowAll((prevShowAll) => !prevShowAll)}
            >
              {showAll ? (
                <EyeInvisibleOutlined {...iconProps} />
              ) : (
                <EyeOutlined {...iconProps} />
              )}
            </button>
          </>
        )}
        
        {copy.show && (
          <CopyTwoTone
            {...iconProps}
            className={s.copy}
            onClick={handleCopy}
          />
        )}
      </div>
    </div>
  );
};

export default Text;